import { useState, useEffect } from 'react';
import { API_BASE } from '../../utils/apiClient';
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  DateInput,
  required,
  useNotify,
  useRedirect,
} from 'react-admin';
import { Typography, Alert, Card, CardContent, Grid, Box, Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { Grid3DConfigForm, type Grid3DConfig } from '../../components/Grid3DConfig';

const gameTypeChoices = [
  { id: 'grid', name: 'Grid (2D)' },
  { id: 'grid_3d', name: 'Grid (3D)' },
  { id: 'higher_lower', name: 'Higher/Lower' },
  { id: 'timeline', name: 'Timeline' },
  { id: 'fact_or_cap', name: 'Fact or Cap' },
  { id: 'odd_one_out', name: 'Odd One Out' },
  { id: 'connections', name: 'Connections' },
  { id: 'guess_the_movie', name: 'Guess the Movie' },
  { id: 'redacted_plot', name: 'Redacted Plot' },
];

const entityTypeChoices = [
  { id: 'cricket_player', name: 'Cricket Player' },
  { id: 'movie', name: 'Movie' },
  { id: 'movie_song', name: 'Movie Song' },
];

// Template for a 2D grid config
const gridConfigTemplate = {
  criteria: {
    x: [
      { label: 'Criterion 1', conditions: [] },
      { label: 'Criterion 2', conditions: [] },
      { label: 'Criterion 3', conditions: [] },
    ],
    y: [
      { label: 'Criterion A', conditions: [] },
      { label: 'Criterion B', conditions: [] },
      { label: 'Criterion C', conditions: [] },
    ],
  },
  answers: [
    [[], [], []],
    [[], [], []],
    [[], [], []],
  ],
};

// JSON input component
const JsonInput = ({ source, label }: { source: string; label: string }) => {
  return (
    <TextInput
      source={source}
      label={label}
      multiline
      fullWidth
      minRows={20}
      maxRows={40}
      format={(value: unknown) =>
        typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || '{}')
      }
      parse={(value: string) => {
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }}
      sx={{
        '& .MuiInputBase-input': {
          fontFamily: 'monospace',
          fontSize: '0.875rem',
        },
      }}
    />
  );
};

// 3D Grid Config Creator (custom form)
const Grid3DConfigCreator = () => {
  const notify = useNotify();
  const redirect = useRedirect();

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [entityType, setEntityType] = useState('cricket_player');
  const [category, setCategory] = useState('ipl');
  const [config, setConfig] = useState<Grid3DConfig | null>(null);
  const [saving, setSaving] = useState(false);

  // Check for pending config from intersection tester
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingGameConfig');
    if (pending) {
      try {
        const data = JSON.parse(pending);
        if (data.entityType) setEntityType(data.entityType);
        if (data.category) setCategory(data.category);
        sessionStorage.removeItem('pendingGameConfig');
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  const handleSave = async () => {
    if (!config || !config.answers) {
      notify('Please validate and compute answers first', { type: 'warning' });
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE}/admin/api/game-configs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          game_type: 'grid_3d',
          entity_type: entityType,
          category,
          config,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save game config');
      }

      notify('Game config created successfully!', { type: 'success' });
      redirect('/game-configs');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save';
      notify(message, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Create 3D Grid Game Config
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextInput
                source="date"
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <SelectInput
                source="entity_type"
                label="Entity Type"
                choices={entityTypeChoices}
                value={entityType}
                onChange={(e) => setEntityType(e.target.value as string)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextInput
                source="category"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                fullWidth
                helperText="e.g., 'ipl', 'tamil', 'hindi'"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || !config?.answers}
                  fullWidth
                >
                  {saving ? 'Saving...' : 'Save Game Config'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid3DConfigForm
        entityType={entityType}
        category={category}
        value={config as Grid3DConfig}
        onChange={setConfig}
      />
    </Box>
  );
};

// Standard form for other game types
const StandardConfigForm = () => {
  return (
    <Create>
      <SimpleForm
        defaultValues={{
          date: new Date().toISOString().split('T')[0],
          config: gridConfigTemplate,
        }}
      >
        <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
          Create a new game configuration. The config JSON structure depends on the game type.
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Game Settings
                </Typography>

                <DateInput
                  source="date"
                  fullWidth
                  required
                  validate={required()}
                  helperText="The date this game will be available"
                />

                <SelectInput
                  source="game_type"
                  choices={gameTypeChoices.filter((c) => c.id !== 'grid_3d')}
                  fullWidth
                  required
                  validate={required()}
                  defaultValue="grid"
                />

                <SelectInput
                  source="entity_type"
                  choices={entityTypeChoices}
                  fullWidth
                  required
                  validate={required()}
                />

                <TextInput
                  source="category"
                  fullWidth
                  helperText="Optional category (e.g., 'ipl', 'tamil', 'hindi')"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Configuration (JSON)
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Define the game configuration including criteria, conditions, and pre-computed answers.
                  The structure varies by game type.
                </Typography>
                <JsonInput source="config" label="Config" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SimpleForm>
    </Create>
  );
};

// Main component - for 3D grid, use the dedicated Grid3DConfigCreate page
export const GameConfigCreate = () => {
  return <StandardConfigForm />;
};

// Separate export for 3D grid creator (can be registered as a separate route)
export const Grid3DGameConfigCreate = Grid3DConfigCreator;
